/**
 * Reusable utility for batch loading table data in chunks (default 250 items),
 * preventing browser and server timeouts/hangs when "All" is selected in page limit.
 */

export async function runBatchLoader({
  fetchPage, // async (page, batchSize) => ({ rows: Array, total: Number })
  batchSize = 100,
  initialTotal = 0,
  onProgress, // ({ active, loaded, total, percent, completed, stopped }) => void
  onRowsLoaded, // (accumulatedRows, totalCount) => void
  cancelRef, // { current: boolean }
  runningRef, // { current: boolean }
}) {
  if (cancelRef) cancelRef.current = false;
  if (runningRef) runningRef.current = true;

  let currentPage = 1;
  let accumulatedRows = [];
  let totalCount = initialTotal || 0;
  let hasMore = true;

  if (onProgress) {
    onProgress({
      active: true,
      loaded: 0,
      total: totalCount,
      percent: 0,
    });
  }

  try {
    while (hasMore && (!cancelRef || !cancelRef.current)) {
      const result = await fetchPage(currentPage, batchSize);
      if (cancelRef && cancelRef.current) break;

      if (result) {
        const newRows = Array.isArray(result)
          ? result
          : Array.isArray(result.rows)
          ? result.rows
          : Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.items)
          ? result.items
          : [];

        const serverTotal =
          result.total_rows !== undefined
            ? result.total_rows
            : result.total_size !== undefined
            ? result.total_size
            : result.totalRows !== undefined
            ? result.totalRows
            : result.total !== undefined
            ? result.total
            : result.count !== undefined
            ? result.count
            : totalCount;

        if (serverTotal !== undefined && serverTotal > 0) {
          totalCount = serverTotal;
        } else if (totalCount === 0) {
          totalCount = accumulatedRows.length + newRows.length;
        }

        const getRowKey = (r, idx) => {
          if (!r) return idx;
          if (r.id !== undefined && r.id !== null) return `id_${r.id}`;
          if (r.order_number) return `order_${r.order_number}`;
          if (r.orderform_number) return `of_${r.orderform_number}`;
          if (r.invoice_number) return `inv_${r.invoice_number}`;
          if (r.proforma_invoice_number) return `prof_${r.proforma_invoice_number}`;
          if (r.client_code) return `code_${r.client_code}`;
          return null;
        };

        const existingKeys = new Set();
        accumulatedRows.forEach((r, idx) => {
          const key = getRowKey(r, idx);
          if (key !== null) existingKeys.add(key);
        });

        const uniqueNewRows = newRows.filter((r, idx) => {
          const key = getRowKey(r, idx);
          if (key !== null) {
            if (existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
          }
          return true;
        });

        accumulatedRows = [...accumulatedRows, ...uniqueNewRows];

        if (onRowsLoaded) {
          onRowsLoaded(accumulatedRows, totalCount);
        }

        const percent =
          totalCount > 0
            ? Math.min(100, Math.round((accumulatedRows.length / totalCount) * 100))
            : 100;

        if (onProgress) {
          onProgress({
            active: true,
            loaded: accumulatedRows.length,
            total: totalCount,
            percent,
          });
        }

        if (
          newRows.length < batchSize ||
          accumulatedRows.length >= totalCount ||
          newRows.length === 0
        ) {
          hasMore = false;
        } else {
          currentPage++;
        }
      } else {
        hasMore = false;
      }
    }

    if (cancelRef && !cancelRef.current && onProgress) {
      onProgress({
        active: false,
        completed: true,
        loaded: accumulatedRows.length,
        total: totalCount,
        percent: 100,
      });
    }
  } catch (err) {
    console.error("Batch loader error:", err);
    if (onProgress) {
      onProgress(prev =>
        prev
          ? {
              ...prev,
              active: false,
              stopped: true,
              loaded: accumulatedRows.length,
              total: totalCount,
            }
          : null
      );
    }
  } finally {
    if (runningRef) runningRef.current = false;
  }

  return accumulatedRows;
}
